import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EstimatorContactFormFields } from '../../../core/estimator/estimator.models';
import { useLocale } from '../../../core/locales/useLocale';
import {
  requiredBoolean,
  requiredString,
  validateEmail,
  validatePhone,
} from '../../../core/shared/forms/common.validator';
import { FormValidations, useForm } from '../../../core/shared/forms/useForm';
import { focusError } from '../../../utils/form';
import { CheckBoxField } from '../../atoms/checkbox-field/CheckBoxField';
import { InputField } from '../../atoms/input-field/InputField';
import { Loading } from '../../atoms/loading/Loading';
import { TelephoneInputField } from '../../atoms/tel-input-field/TelephoneInputField';

export const ValContatti: React.FC<{
  onForward: (f: EstimatorContactFormFields) => void;
  loading: boolean;
  isExternal?: boolean;
}> = ({ onForward, loading = false, isExternal = false }) => {
  const { t } = useTranslation(['valutatore']);
  const { t: tc } = useTranslation();
  const [generalError, setGeneralError] = useState<string>('');
  const { locale } = useLocale();

  const initForm: EstimatorContactFormFields = {
    givenName: '',
    familyName: '',
    phoneNumber: '',
    email: '',
    flgMarketing: {
      value: false,
      text: tc('pdp.contactForm.marketing'),
    },
    flgGeneralConditionPrivacyPolicy: {
      value: false,
      text: tc('pdp.contactForm.privacyAndGeneralCondition', {
        generalConditionLink: '/terms',
        privacyPolicyLink: '/privacy',
      }),
    },
  };

  const formValidation: FormValidations<EstimatorContactFormFields> = {
    givenName: [requiredString(tc)],
    familyName: [requiredString(tc)],
    phoneNumber: [requiredString(tc), validatePhone(tc)],
    email: [requiredString(tc), validateEmail(tc)],
    flgGeneralConditionPrivacyPolicy: {
      value: [requiredBoolean(tc)],
    },
  };

  const { form, errors, validate, submit } = useForm(
    'onSubmit',
    initForm,
    formValidation
  );

  const onFieldUpdate = (
    newPartialForm: Partial<EstimatorContactFormFields>
  ) => {
    const newForm = { ...form, ...newPartialForm };
    validate(newForm);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (submit()) {
          onForward(form);
        } else {
          focusError();
        }
      }}
    >
      <div className="valutatore__container__title">
        {t(isExternal ? 'step_six.title_ext' : 'step_six.title')}
      </div>
      <div className="valutatore__container--two_columns">
        <InputField
          type="text"
          name="firstname"
          label={tc('login.givenNameLabel')}
          placeholder={tc('login.givenNamePlaceholder')}
          value={form.givenName}
          errors={errors.givenName}
          onChange={(givenName: string) => onFieldUpdate({ givenName })}
        />

        <InputField
          type="text"
          name="lastname"
          label={tc('login.familyNameLabel')}
          placeholder={tc('login.familyNamePlaceholder')}
          value={form.familyName}
          errors={errors.familyName}
          onChange={(familyName: string) => onFieldUpdate({ familyName })}
        />
        <InputField
          type="text"
          name="email"
          label={tc('login.emailLabel')}
          placeholder={tc('login.emailPlaceholder')}
          value={form.email}
          errors={errors.email}
          onChange={(email: string) => onFieldUpdate({ email })}
        />
        <TelephoneInputField
          label={tc('login.phoneNumberLabel')}
          placeholder={tc('login.phoneNumberPlaceholder')}
          value={form.phoneNumber}
          errors={errors.phoneNumber}
          onChange={(phoneNumber: string) => onFieldUpdate({ phoneNumber })}
        />

        <p className="assistive_text assistive_text--error">{generalError}</p>
      </div>
      <div className="valutatore__container__checkboxes">
        <CheckBoxField
          label={
            <span
              dangerouslySetInnerHTML={{
                __html: form.flgGeneralConditionPrivacyPolicy.text,
              }}
            ></span>
          }
          checked={form.flgGeneralConditionPrivacyPolicy.value}
          errors={errors.flgGeneralConditionPrivacyPolicy.value}
          onChange={(value) => {
            onFieldUpdate({
              ...form,
              flgGeneralConditionPrivacyPolicy: {
                ...form.flgGeneralConditionPrivacyPolicy,
                value,
              },
            });
          }}
        />
        <CheckBoxField
          label={form.flgMarketing.text}
          checked={form.flgMarketing.value}
          onChange={(value) => {
            onFieldUpdate({
              ...form,
              flgMarketing: { ...form.flgMarketing, value },
            });
          }}
        />
      </div>
      <button
        type="submit"
        className="valutatore__container__button_forward valutatore__container__button_forward--accent"
      >
        {tc('confirm')}
      </button>
    </form>
  );
};
